import Time from 'components/base/Time'
import Header from 'components/landing/Header'
import PageMeta from 'components/base/PageMeta'
import { Theme, Themes } from 'configs/theme'
import { Context } from 'lib/api'
import { FC } from 'lib/component-utils'
import { ContextDTO, themeFromHostname, withStaticProps } from 'lib/contextCreation'
import { BLArticle, Mall } from 'lib/dto'
import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { getCanonicalHref } from 'lib/canonical'

export const getStaticProps: GetStaticProps = withStaticProps(
  async ({ pageContext, userContext }) => {
    const slug = pageContext.params!.slug as string
    const article = await userContext.getSellematicsArticleBySlug(slug)

    pageContext.locale = article.lang

    if (!article) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        article,
        isResponsive: true,
      },
    }
  },
  Themes.SELLEMATICS
)

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
  const ctx: ContextDTO = {
    cookie: null,
    currentMall: { code: 'wb', description: 'wb', currency: 'RUB' } as Mall,
    host: `localhost:${process.env.PORT ?? 4000}`,
    // process.env.NODE_ENV === 'development'
    //   ? `localhost:${process.env.PORT ?? 4000}`
    //   : 'sellematics.com',
    isAuthorized: false,
    hasAvailableAuthorization: false,
    malls: [],
    user: null,
    ip: '',
    userAgent: '',
    theme: Themes.SELLEMATICS,
    lang: 'ru',
    lastLoginType: null,
    lastLoginDate: null,
  }
  const userContext = new Context(ctx)

  const ruArticles = await userContext.getSellematicsArticles(1, 100, 'ru')
  const enArticles = await userContext.getSellematicsArticles(1, 100, 'en')
  const paths = ruArticles.items
    .concat(enArticles.items)
    .map((article) => ({ params: { slug: article.slug } }))

  return {
    paths,
    fallback: 'blocking',
  }
}

type Props = {
  article: BLArticle
}

const ArticlePage: FC<Props> = ({ article }) => {
  const [theme, setTheme] = useState<Theme>(Themes.SELLEMATICS)
  const router = useRouter()
  const canonicalHref = getCanonicalHref(theme.canonicalURL, router.asPath || `/articles/${article.slug}/`)

  useEffect(() => {
    const theme = themeFromHostname(window.location.hostname)
    setTheme(theme)
  }, [])

  return (
    <div>
      <PageMeta
        title={`${article.name} | ${theme?.title}`}
        description={article.description}
        canonical={canonicalHref}
        image={article.image.big.replace('_big', '_default_big')}
        type="article"
        articlePublishedTime={article.createdAt}
      />
      <Header />
      <div className="px-8 pt-12 pb-24 md:px-24">
        <div className="flex flex-col items-center max-w-3xl mx-auto">
          <Time
            dateTime={article.createdAt}
            options={{ dateStyle: 'long' }}
            className="text-base text-gray-500"
          />
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-gray-800 md:text-5xl max-w-prose">
            {article.name}
          </h1>
          <p className="mt-8 text-xl md:text-2xl max-w-prose">{article.description}</p>
          <article
            className="mt-12 prose text-black md:prose-lg !max-w-full"
            dangerouslySetInnerHTML={{ __html: article.text }}
          />
        </div>
      </div>
    </div>
  )
}

export default ArticlePage
